import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

interface AsyncState<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

/**
 * Renders loading skeletons / an error card / the resolved content for a
 * transcript page's data. Keeps the loading + error UX identical across the
 * Tax Owed, Payments, Income, FTA, and Bankruptcy tabs.
 */
export function AsyncContent<T>({
  state,
  children,
}: {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
}) {
  if (state.loading) {
    return (
      <Card className="gap-3 p-5">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </Card>
    );
  }
  if (state.error) {
    return (
      <Card className="flex-col items-center gap-3 p-8 text-center">
        <p className="text-muted-foreground text-sm">{state.error}</p>
        <Button variant="outline" size="sm" onClick={state.reload}>
          Try again
        </Button>
      </Card>
    );
  }
  if (state.data === undefined) return null;
  return <>{children(state.data)}</>;
}
